import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
    Zap, Plus, Loader2, Play, Pause, Trash2, Edit3, Users, Mail, Phone,
    Clock, ChevronRight, Sparkles, Target, Send, ArrowLeft, Eye,
    GripVertical, X, ChevronDown, CheckCircle2, AlertCircle, SkipForward,
    MessageSquare,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
    draft: 'bg-stone-100 text-stone-600',
    active: 'bg-emerald-50 text-emerald-700',
    paused: 'bg-amber-50 text-amber-700',
    completed: 'bg-blue-50 text-blue-700',
    archived: 'bg-stone-100 text-stone-400',
};

const STEP_TYPES = [
    { type: 'email', label: 'Email', icon: Mail, color: 'text-blue-600 bg-blue-50' },
    { type: 'whatsapp', label: 'WhatsApp', icon: Phone, color: 'text-emerald-600 bg-emerald-50' },
    { type: 'ai_email', label: 'AI Email', icon: Sparkles, color: 'text-amber-600 bg-amber-50' },
    { type: 'ai_whatsapp', label: 'AI WhatsApp', icon: Sparkles, color: 'text-purple-600 bg-purple-50' },
    { type: 'wait', label: 'Wait', icon: Clock, color: 'text-stone-600 bg-stone-100' },
];

const STEP_TYPE_MAP = Object.fromEntries(STEP_TYPES.map(s => [s.type, s]));

// ─── Campaign Detail / Editor ───────────────────────────────────────────────

function CampaignDetail({ campaignId, onBack, getAuthHeader }) {
    const [campaign, setCampaign] = useState(null);
    const [enrollments, setEnrollments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [enrolling, setEnrolling] = useState(false);
    const [previewTargets, setPreviewTargets] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const [activeSection, setActiveSection] = useState('steps'); // steps | targets | enrollments

    const loadCampaign = useCallback(async () => {
        try {
            const [campRes, enrollRes] = await Promise.all([
                axios.get(`${API}/ats/activation-campaigns/${campaignId}`, { headers: getAuthHeader() }),
                axios.get(`${API}/ats/activation-campaigns/${campaignId}/enrollments?page_size=100`, { headers: getAuthHeader() }),
            ]);
            setCampaign(campRes.data);
            setEnrollments(enrollRes.data?.data || []);
        } catch { toast.error('Failed to load campaign'); }
        finally { setLoading(false); }
    }, [campaignId, getAuthHeader]);

    useEffect(() => { loadCampaign(); }, [loadCampaign]);

    const saveCampaign = async (updates) => {
        setSaving(true);
        try {
            const res = await axios.put(`${API}/ats/activation-campaigns/${campaignId}`, updates, { headers: getAuthHeader() });
            setCampaign(res.data);
            toast.success('Campaign saved');
        } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
        finally { setSaving(false); }
    };

    const toggleStatus = async () => {
        const newStatus = campaign.status === 'active' ? 'paused' : 'active';
        await saveCampaign({ status: newStatus });
    };

    const previewTarget = async () => {
        setPreviewing(true);
        try {
            const res = await axios.post(`${API}/ats/activation-campaigns/${campaignId}/preview-targets`, {}, { headers: getAuthHeader() });
            setPreviewTargets(res.data);
        } catch (err) { toast.error(err.response?.data?.detail || 'Preview failed'); }
        finally { setPreviewing(false); }
    };

    const enrollFromFilters = async () => {
        setEnrolling(true);
        try {
            const res = await axios.post(`${API}/ats/activation-campaigns/${campaignId}/enroll-from-filters`, {}, { headers: getAuthHeader() });
            toast.success(`Enrolled ${res.data.enrolled} applicants`);
            loadCampaign();
        } catch (err) { toast.error(err.response?.data?.detail || 'Enrollment failed'); }
        finally { setEnrolling(false); }
    };

    // ─── Step Editor ───
    const addStep = (type) => {
        const steps = [...(campaign.steps || [])];
        const defaultConfig = type === 'wait' ? { days: 1, hours: 0 }
            : type === 'email' ? { subject: '', body: '' }
            : type === 'whatsapp' ? { body: '' }
            : type === 'ai_email' ? { purpose: 'outreach', custom_instructions: '' }
            : type === 'ai_whatsapp' ? { purpose: 'outreach', custom_instructions: '' }
            : {};
        steps.push({ step_id: '', step_type: type, order: steps.length, config: defaultConfig });
        saveCampaign({ steps });
    };

    const updateStep = (index, updates) => {
        const steps = [...(campaign.steps || [])];
        steps[index] = { ...steps[index], ...updates };
        saveCampaign({ steps });
    };

    const removeStep = (index) => {
        const steps = (campaign.steps || []).filter((_, i) => i !== index);
        saveCampaign({ steps });
    };

    // ─── Target Filter Editor ───
    const updateFilters = (updates) => {
        const current = campaign.target_filters || {};
        saveCampaign({ target_filters: { ...current, ...updates } });
    };

    if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>;
    if (!campaign) return null;

    const filters = campaign.target_filters || {};

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                    <button onClick={onBack} className="p-2 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-600 transition-colors">
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div>
                        <input
                            value={campaign.name}
                            onChange={e => setCampaign(c => ({ ...c, name: e.target.value }))}
                            onBlur={() => saveCampaign({ name: campaign.name })}
                            className="text-xl font-bold text-stone-900 bg-transparent border-none outline-none focus:ring-0 p-0"
                            style={{ fontFamily: 'Manrope, sans-serif' }}
                        />
                        <div className="flex items-center gap-2 mt-1">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[campaign.status] || STATUS_COLORS.draft}`}>
                                {campaign.status}
                            </span>
                            <span className="text-xs text-stone-400">{campaign.stats?.total_enrolled || 0} enrolled</span>
                            <span className="text-xs text-stone-400">{campaign.stats?.completed || 0} completed</span>
                        </div>
                    </div>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={toggleStatus} disabled={saving}>
                        {campaign.status === 'active' ? <><Pause className="w-4 h-4 mr-1" /> Pause</> : <><Play className="w-4 h-4 mr-1" /> Activate</>}
                    </Button>
                </div>
            </div>

            {/* Section Tabs */}
            <div className="flex gap-1 bg-stone-100 rounded-lg p-0.5 w-fit">
                {[
                    { key: 'steps', label: 'Steps', icon: Zap },
                    { key: 'targets', label: 'Targeting', icon: Target },
                    { key: 'enrollments', label: `Enrolled (${enrollments.length})`, icon: Users },
                ].map(s => {
                    const Icon = s.icon;
                    return (
                        <button key={s.key} onClick={() => setActiveSection(s.key)}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1.5 ${
                                activeSection === s.key ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'
                            }`}>
                            <Icon className="w-3.5 h-3.5" /> {s.label}
                        </button>
                    );
                })}
            </div>

            {/* ── Steps Section ── */}
            {activeSection === 'steps' && (
                <div className="space-y-3">
                    {(campaign.steps || []).map((step, idx) => {
                        const meta = STEP_TYPE_MAP[step.step_type] || STEP_TYPES[0];
                        const Icon = meta.icon;
                        return (
                            <div key={step.step_id || idx} className="bg-white rounded-xl border border-stone-200 p-4">
                                <div className="flex items-start gap-3">
                                    <div className="flex items-center gap-2 flex-shrink-0 pt-1">
                                        <span className="text-xs font-bold text-stone-400 w-5 text-center">{idx + 1}</span>
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${meta.color}`}>
                                            <Icon className="w-4 h-4" />
                                        </div>
                                    </div>
                                    <div className="flex-1 min-w-0 space-y-2">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-medium text-stone-700">{meta.label}</span>
                                            <button onClick={() => removeStep(idx)} className="text-stone-400 hover:text-red-500 transition-colors">
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>

                                        {step.step_type === 'wait' && (
                                            <div className="flex items-center gap-2">
                                                <input type="number" min="0" value={step.config?.days || 0}
                                                    onChange={e => updateStep(idx, { config: { ...step.config, days: parseInt(e.target.value) || 0 } })}
                                                    className="w-16 px-2 py-1 text-sm border border-stone-200 rounded-lg" />
                                                <span className="text-xs text-stone-500">days</span>
                                                <input type="number" min="0" max="23" value={step.config?.hours || 0}
                                                    onChange={e => updateStep(idx, { config: { ...step.config, hours: parseInt(e.target.value) || 0 } })}
                                                    className="w-16 px-2 py-1 text-sm border border-stone-200 rounded-lg" />
                                                <span className="text-xs text-stone-500">hours</span>
                                            </div>
                                        )}

                                        {step.step_type === 'email' && (
                                            <div className="space-y-2">
                                                <input placeholder="Subject line (supports {{Name}}, {{JobTitle}}...)"
                                                    value={step.config?.subject || ''}
                                                    onChange={e => updateStep(idx, { config: { ...step.config, subject: e.target.value } })}
                                                    className="w-full px-3 py-1.5 text-sm border border-stone-200 rounded-lg" />
                                                <textarea placeholder="Email body..."
                                                    value={step.config?.body || ''}
                                                    onChange={e => updateStep(idx, { config: { ...step.config, body: e.target.value } })}
                                                    rows={4} className="w-full px-3 py-1.5 text-sm border border-stone-200 rounded-lg resize-none" />
                                                <p className="text-[10px] text-stone-400">Merge fields: {'{{Name}} {{FirstName}} {{Email}} {{Headline}} {{City}} {{JobTitle}} {{Company}}'}</p>
                                            </div>
                                        )}

                                        {step.step_type === 'whatsapp' && (
                                            <div className="space-y-2">
                                                <textarea placeholder="WhatsApp message..."
                                                    value={step.config?.body || ''}
                                                    onChange={e => updateStep(idx, { config: { ...step.config, body: e.target.value } })}
                                                    rows={3} className="w-full px-3 py-1.5 text-sm border border-stone-200 rounded-lg resize-none" />
                                                <p className="text-[10px] text-stone-400">Merge fields: {'{{Name}} {{FirstName}} {{JobTitle}} {{Company}}'}</p>
                                            </div>
                                        )}

                                        {(step.step_type === 'ai_email' || step.step_type === 'ai_whatsapp') && (
                                            <div className="space-y-2">
                                                <select value={step.config?.purpose || 'outreach'}
                                                    onChange={e => updateStep(idx, { config: { ...step.config, purpose: e.target.value } })}
                                                    className="w-full px-3 py-1.5 text-sm border border-stone-200 rounded-lg bg-white">
                                                    <option value="outreach">Initial outreach</option>
                                                    <option value="follow_up">Follow-up</option>
                                                    <option value="opportunity_share">Share opportunity details</option>
                                                    <option value="re_engage">Re-engage inactive talent</option>
                                                </select>
                                                <input placeholder="Additional instructions for AI (optional)"
                                                    value={step.config?.custom_instructions || ''}
                                                    onChange={e => updateStep(idx, { config: { ...step.config, custom_instructions: e.target.value } })}
                                                    className="w-full px-3 py-1.5 text-sm border border-stone-200 rounded-lg" />
                                                <p className="text-[10px] text-stone-400 flex items-center gap-1">
                                                    <Sparkles className="w-3 h-3" /> AI will generate personalised content for each applicant at send time
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Connector line */}
                                {idx < (campaign.steps || []).length - 1 && (
                                    <div className="flex justify-center mt-3">
                                        <div className="w-0.5 h-4 bg-stone-200" />
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {/* Add Step */}
                    <div className="bg-stone-50 rounded-xl border-2 border-dashed border-stone-200 p-4">
                        <p className="text-xs font-medium text-stone-500 mb-2">Add step</p>
                        <div className="flex flex-wrap gap-2">
                            {STEP_TYPES.map(st => {
                                const Icon = st.icon;
                                return (
                                    <button key={st.type} onClick={() => addStep(st.type)}
                                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-stone-200 bg-white hover:bg-stone-50 text-stone-700 transition-colors">
                                        <Icon className="w-3.5 h-3.5" /> {st.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Targeting Section ── */}
            {activeSection === 'targets' && (
                <div className="space-y-4">
                    <div className="bg-white rounded-xl border border-stone-200 p-5 space-y-4">
                        <h3 className="text-sm font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Target Filters</h3>

                        {/* Status */}
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Statuses</label>
                            <div className="flex flex-wrap gap-2">
                                {['active', 'recall', 'inactive'].map(s => (
                                    <button key={s} onClick={() => {
                                        const current = filters.statuses || [];
                                        updateFilters({ statuses: current.includes(s) ? current.filter(x => x !== s) : [...current, s] });
                                    }} className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                                        (filters.statuses || []).includes(s) ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-600 border-stone-200'
                                    }`}>{s}</button>
                                ))}
                            </div>
                        </div>

                        {/* Tags */}
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Tags (comma-separated)</label>
                            <input value={(filters.tags || []).join(', ')}
                                onChange={e => updateFilters({ tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
                                placeholder="e.g. senior, tech, remote"
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg" />
                        </div>

                        {/* Skills */}
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Skills (comma-separated)</label>
                            <input value={(filters.skills || []).join(', ')}
                                onChange={e => updateFilters({ skills: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
                                placeholder="e.g. Python, React, Project Management"
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg" />
                        </div>

                        {/* Locations */}
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Locations (comma-separated)</label>
                            <input value={(filters.locations || []).join(', ')}
                                onChange={e => updateFilters({ locations: e.target.value.split(',').map(t => t.trim()).filter(Boolean) })}
                                placeholder="e.g. Manila, Philippines, Singapore"
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg" />
                        </div>

                        {/* Availability */}
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Availability</label>
                            <div className="flex flex-wrap gap-2">
                                {['immediate', '2_weeks', '1_month', 'other'].map(a => (
                                    <button key={a} onClick={() => {
                                        const current = filters.availability || [];
                                        updateFilters({ availability: current.includes(a) ? current.filter(x => x !== a) : [...current, a] });
                                    }} className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                                        (filters.availability || []).includes(a) ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-600 border-stone-200'
                                    }`}>{a.replace(/_/g, ' ')}</button>
                                ))}
                            </div>
                        </div>

                        {/* Min Profile Completeness */}
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Min Profile Completeness (%)</label>
                            <input type="number" min="0" max="100" value={filters.min_profile_completeness || 0}
                                onChange={e => updateFilters({ min_profile_completeness: parseInt(e.target.value) || 0 })}
                                className="w-24 px-3 py-2 text-sm border border-stone-200 rounded-lg" />
                        </div>
                    </div>

                    {/* Preview */}
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={previewTarget} disabled={previewing}>
                            {previewing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
                            Preview Matches
                        </Button>
                        {campaign.status === 'active' && (
                            <Button size="sm" onClick={enrollFromFilters} disabled={enrolling} className="bg-stone-900 text-white hover:bg-stone-800">
                                {enrolling ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Users className="w-4 h-4 mr-1" />}
                                Enroll Matches
                            </Button>
                        )}
                    </div>

                    {previewTargets && (
                        <div className="bg-white rounded-xl border border-stone-200 p-4">
                            <p className="text-sm font-medium text-stone-900 mb-2">
                                <Target className="w-4 h-4 inline mr-1" />
                                {previewTargets.total} applicant{previewTargets.total !== 1 ? 's' : ''} match
                            </p>
                            {previewTargets.sample.length > 0 && (
                                <div className="space-y-1 mt-2">
                                    {previewTargets.sample.map(a => (
                                        <div key={a.applicant_id} className="flex items-center gap-2 text-xs py-1">
                                            <div className="w-6 h-6 rounded-full bg-stone-100 flex items-center justify-center text-stone-600 font-bold text-[10px]">
                                                {(a.name || '?')[0]}
                                            </div>
                                            <span className="text-stone-700 font-medium">{a.name}</span>
                                            <span className="text-stone-400">{a.email}</span>
                                            {(a.tags || []).slice(0, 3).map(t => (
                                                <span key={t} className="px-1.5 py-0.5 bg-stone-100 text-stone-500 rounded text-[10px]">{t}</span>
                                            ))}
                                        </div>
                                    ))}
                                    {previewTargets.total > 20 && (
                                        <p className="text-[10px] text-stone-400 mt-1">Showing 20 of {previewTargets.total}</p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* ── Enrollments Section ── */}
            {activeSection === 'enrollments' && (
                <div className="space-y-2">
                    {enrollments.length === 0 ? (
                        <div className="text-center py-12 bg-white rounded-xl border border-stone-200">
                            <Users className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                            <p className="text-sm text-stone-500">No enrollments yet</p>
                            <p className="text-xs text-stone-400 mt-1">Use targeting filters to enroll applicants, or bulk enroll from the Talent Pool</p>
                        </div>
                    ) : (
                        enrollments.map(e => {
                            const stepCount = campaign.steps?.length || 0;
                            const currentIdx = e.current_step_index || 0;
                            const pct = stepCount > 0 ? Math.round((Math.min(currentIdx, stepCount) / stepCount) * 100) : 0;
                            const ENROLL_STATUS_ICONS = {
                                active: <Play className="w-3 h-3 text-emerald-500" />,
                                completed: <CheckCircle2 className="w-3 h-3 text-blue-500" />,
                                failed: <AlertCircle className="w-3 h-3 text-red-500" />,
                                paused: <Pause className="w-3 h-3 text-amber-500" />,
                            };
                            return (
                                <div key={e.enrollment_id} className="bg-white rounded-xl border border-stone-200 p-3 flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center text-stone-600 font-bold text-xs flex-shrink-0">
                                        {(e.applicant_name || '?')[0]}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-stone-800 truncate">{e.applicant_name}</span>
                                            {ENROLL_STATUS_ICONS[e.status]}
                                        </div>
                                        <div className="flex items-center gap-2 mt-1">
                                            <div className="w-24 h-1.5 bg-stone-100 rounded-full overflow-hidden">
                                                <div className={`h-full rounded-full ${e.status === 'completed' ? 'bg-blue-500' : 'bg-emerald-500'}`}
                                                    style={{ width: `${pct}%` }} />
                                            </div>
                                            <span className="text-[10px] text-stone-400">
                                                Step {Math.min(currentIdx + 1, stepCount)}/{stepCount}
                                            </span>
                                        </div>
                                    </div>
                                    <span className="text-xs text-stone-400">{e.applicant_email}</span>
                                </div>
                            );
                        })
                    )}
                </div>
            )}
        </div>
    );
}


// ─── Main Page ──────────────────────────────────────────────────────────────

export default function ATSActivationCampaigns() {
    const { getAuthHeader } = useAuth();
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [creating, setCreating] = useState(false);
    const [createForm, setCreateForm] = useState({ name: '', description: '' });
    const [selectedId, setSelectedId] = useState(null);
    const [jobs, setJobs] = useState([]);
    const [selectedJob, setSelectedJob] = useState('');

    const loadCampaigns = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/ats/activation-campaigns?page_size=100`, { headers: getAuthHeader() });
            setCampaigns(res.data?.data || []);
        } catch { toast.error('Failed to load campaigns'); }
        finally { setLoading(false); }
    }, [getAuthHeader]);

    const loadJobs = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/ats/jobs?status=active&page_size=100`, { headers: getAuthHeader() });
            setJobs(res.data?.data || []);
        } catch { /* ignore */ }
    }, [getAuthHeader]);

    useEffect(() => { loadCampaigns(); loadJobs(); }, [loadCampaigns, loadJobs]);

    const handleCreate = async () => {
        if (!createForm.name.trim()) return toast.error('Campaign name is required');
        setCreating(true);
        try {
            const res = await axios.post(`${API}/ats/activation-campaigns`, {
                name: createForm.name,
                description: createForm.description,
                job_id: selectedJob || null,
            }, { headers: getAuthHeader() });
            toast.success('Campaign created');
            setShowCreate(false);
            setCreateForm({ name: '', description: '' });
            setSelectedJob('');
            setSelectedId(res.data.campaign_id);
            loadCampaigns();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create'); }
        finally { setCreating(false); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this campaign and all enrollments?')) return;
        try {
            await axios.delete(`${API}/ats/activation-campaigns/${id}`, { headers: getAuthHeader() });
            toast.success('Campaign deleted');
            loadCampaigns();
        } catch { toast.error('Failed to delete'); }
    };

    // Detail view
    if (selectedId) {
        return <CampaignDetail campaignId={selectedId} onBack={() => { setSelectedId(null); loadCampaigns(); }} getAuthHeader={getAuthHeader} />;
    }

    if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>;

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Activation Campaigns</h1>
                    <p className="text-sm text-stone-500 mt-1">Outreach sequences to engage talent from your pool</p>
                </div>
                <Button onClick={() => setShowCreate(true)} className="bg-stone-900 text-white hover:bg-stone-800">
                    <Plus className="w-4 h-4 mr-1" /> New Campaign
                </Button>
            </div>

            {/* Campaign List */}
            {campaigns.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-xl border border-stone-200">
                    <Zap className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                    <p className="text-stone-500 font-medium">No activation campaigns yet</p>
                    <p className="text-sm text-stone-400 mt-1">Create a campaign to start engaging talent from your pool</p>
                    <Button onClick={() => setShowCreate(true)} className="mt-4 bg-stone-900 text-white hover:bg-stone-800">
                        <Plus className="w-4 h-4 mr-1" /> Create Campaign
                    </Button>
                </div>
            ) : (
                <div className="space-y-3">
                    {campaigns.map(c => (
                        <div key={c.campaign_id}
                            onClick={() => setSelectedId(c.campaign_id)}
                            className="bg-white rounded-xl border border-stone-200 p-4 hover:border-stone-300 hover:bg-stone-50/50 cursor-pointer transition-all group">
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center flex-shrink-0">
                                    <Zap className="w-5 h-5 text-amber-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <p className="text-sm font-medium text-stone-900">{c.name}</p>
                                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[c.status] || STATUS_COLORS.draft}`}>
                                            {c.status}
                                        </span>
                                    </div>
                                    {c.description && <p className="text-xs text-stone-400 mt-0.5 truncate">{c.description}</p>}
                                    <div className="flex items-center gap-4 mt-1.5">
                                        <span className="text-xs text-stone-500 flex items-center gap-1">
                                            <Zap className="w-3 h-3" /> {(c.steps || []).length} step{(c.steps || []).length !== 1 ? 's' : ''}
                                        </span>
                                        <span className="text-xs text-stone-500 flex items-center gap-1">
                                            <Users className="w-3 h-3" /> {c.stats?.total_enrolled || 0} enrolled
                                        </span>
                                        <span className="text-xs text-stone-500 flex items-center gap-1">
                                            <CheckCircle2 className="w-3 h-3" /> {c.stats?.completed || 0} completed
                                        </span>
                                    </div>
                                </div>
                                <button onClick={e => { e.stopPropagation(); handleDelete(c.campaign_id); }}
                                    className="p-2 text-stone-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all">
                                    <Trash2 className="w-4 h-4" />
                                </button>
                                <ChevronRight className="w-4 h-4 text-stone-300 group-hover:text-stone-500" />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create Dialog */}
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogContent className="max-w-md">
                    <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>New Activation Campaign</DialogTitle></DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Campaign Name *</label>
                            <input value={createForm.name} onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))}
                                placeholder="e.g. Senior Developers — Manila Q2"
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/10" />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Description</label>
                            <textarea value={createForm.description} onChange={e => setCreateForm(f => ({ ...f, description: e.target.value }))}
                                placeholder="Brief description of the campaign goal..."
                                rows={2} className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg resize-none" />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1">Linked Job Order (optional)</label>
                            <select value={selectedJob} onChange={e => setSelectedJob(e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white">
                                <option value="">No linked job</option>
                                {jobs.map(j => <option key={j.job_id} value={j.job_id}>{j.title}</option>)}
                            </select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
                        <Button onClick={handleCreate} disabled={creating} className="bg-stone-900 text-white hover:bg-stone-800">
                            {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
                            Create
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
